import { useEffect } from 'react'
import { PaymentMethodType } from '../../model'
import { PCL_CARD_EXPIRY_LIMIT_IN_MONTHS } from '../../utils/credit-card'
import { useAppDispatch } from '../hooks'
import { useCheckoutState, usePreferredCard } from '../checkout/hooks'
import { setCardsForExpiryLimit } from '../checkout/reducer'
import { useHasUserInitiatedChangedPaymentMethod, useSelectedPaymentMethod } from '../topaz/hooks'
import { selectPaymentMethod } from '../topaz/reducer'

/**
 * Enforces the PCL (Pay Monthly / consumer-lending) debit-card expiry limit on a summary page:
 *   1. recalculates saved-card error reasons against `PCL_CARD_EXPIRY_LIMIT_IN_MONTHS`, and
 *   2. keeps the selected payment method in sync when the recalculation auto-switches to a valid card.
 *
 * Shared by every PCL summary (Pay Monthly, PM redesign, Apple Pay Later CL, Apple Retail) so these
 * mechanics live in one place. Each page still surfaces the expiry error itself — the legacy
 * summaries via their local notification effect, the inline/Apple Retail flows via `SummaryNotification`.
 *
 * NOTE: the auto-switch dispatches `selectPaymentMethod` (reducer-only, no Topaz `setupPurchase`).
 * Fixing that to run Topaz setup (`selectPurchaseMethod`) is tracked in XO-6240 — now a single-site
 * change here rather than four.
 */
export const useEnforcePclCardExpiryLimit = (): void => {
  const dispatch = useAppDispatch()
  const { error } = useCheckoutState()
  const { card } = usePreferredCard() ?? {}
  const selectedPaymentMethod = useSelectedPaymentMethod()
  const userInitiatedChangedPaymentMethod = useHasUserInitiatedChangedPaymentMethod()

  // Recalculate saved cards' error reasons against the PCL expiry limit. Skipped once the user has
  // manually changed payment method so we don't override their choice.
  useEffect(() => {
    if (error == null && !userInitiatedChangedPaymentMethod) {
      dispatch(setCardsForExpiryLimit(PCL_CARD_EXPIRY_LIMIT_IN_MONTHS))
    }
  }, [error])

  // Keep the selected payment method in sync after setCardsForExpiryLimit auto-switches to a valid
  // card (the selected method is not updated by the recalculation itself).
  useEffect(() => {
    if (!userInitiatedChangedPaymentMethod && card != null && card?.error === null && selectedPaymentMethod?.type === PaymentMethodType.CREDIT_CARD) {
      dispatch(
        selectPaymentMethod({
          card: {
            token: card.token,
          },
          type: PaymentMethodType.CREDIT_CARD,
        })
      )
    }
  }, [card])
}
